import { useEffect, useState } from 'react'
import { Outlet, Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useClientStore } from '@/stores/clientStore'
import { documentApi } from '@/services/api'
import {
  LayoutDashboard, Users, BookOpen, Building2, FileText, Receipt,
  Settings, LogOut, ArrowLeft, BarChart3, CreditCard,
  Search, Bell, BookOpenCheck, ShieldCheck, BookMarked, SlidersHorizontal,
  FolderOpen, DollarSign, TrendingUp, Boxes, Tag, Activity, PieChart, ClipboardList, UserCircle, ScrollText,
  Menu, Plus, X,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

export default function FirmLayout() {
  const { user, currentFirm, firms, setCurrentFirm, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { clientId } = useParams()
  const currentClient = useClientStore((s) => s.currentClient)
  const [inboxCount, setInboxCount] = useState(0)
  const [sidebarOpen, setSidebarOpen] = useState(true)

  const isClientContext = !!clientId
  const isPremium = currentClient?.plan_tier === 'premium'

  // Fetch document inbox count for sidebar badge
  useEffect(() => {
    if (!clientId) { setInboxCount(0); return }
    documentApi.list(clientId, { status: 'inbox' }).then((res) => {
      setInboxCount(res.data.inbox_count ?? 0)
    }).catch(() => {})
  }, [clientId, location.pathname])

  function isActive(path: string) {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const firmNav = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/clients', icon: Users, label: 'Clients' },
    { to: '/firm-reports', icon: PieChart, label: 'Firm Reports' },
    { to: '/activity', icon: Activity, label: 'Activity' },
  ]

  const clientNavGroups = clientId ? [
    {
      label: null,
      items: [
        { to: `/clients/${clientId}/dashboard`, icon: LayoutDashboard, label: 'Dashboard' },
      ],
    },
    {
      label: 'ACCOUNTING',
      items: [
        { to: `/clients/${clientId}/accounts`, icon: BookOpen, label: 'Chart of Accounts' },
        { to: `/clients/${clientId}/banking`, icon: Building2, label: 'Bank Accounts' },
        { to: `/clients/${clientId}/banking/rules`, icon: ShieldCheck, label: 'Recon. Rules' },
        { to: `/clients/${clientId}/transactions`, icon: Receipt, label: 'Transactions' },
        { to: `/clients/${clientId}/journals`, icon: BookMarked, label: 'Journals' },
        { to: `/clients/${clientId}/contacts`, icon: UserCircle, label: 'Contacts' },
      ],
    },
    ...(isPremium ? [{
      label: 'LEDGER ITEMS' as const,
      items: [
        { to: `/clients/${clientId}/invoices`, icon: FileText, label: 'Invoices' },
        { to: `/clients/${clientId}/bills`, icon: Receipt, label: 'Bills' },
        { to: `/clients/${clientId}/purchase-orders`, icon: ClipboardList, label: 'Purchase Orders' },
        { to: `/clients/${clientId}/quotes`, icon: ScrollText, label: 'Quotes' },
      ],
    }] : []),
    {
      label: 'REPORTS',
      items: [
        { to: `/clients/${clientId}/reports/profit-loss`, icon: BarChart3, label: 'Profit & Loss' },
        { to: `/clients/${clientId}/reports/balance-sheet`, icon: FileText, label: 'Balance Sheet' },
        { to: `/clients/${clientId}/reports/trial-balance`, icon: BookOpenCheck, label: 'Trial Balance' },
        { to: `/clients/${clientId}/reports/cash-summary`, icon: CreditCard, label: 'Cash Summary' },
      ],
    },
    {
      label: 'TOOLS',
      items: [
        { to: `/clients/${clientId}/documents`, icon: FolderOpen, label: 'Documents', badge: inboxCount },
        ...(isPremium ? [
          { to: `/clients/${clientId}/budgets`, icon: DollarSign, label: 'Budgets' },
          { to: `/clients/${clientId}/budgets/variance`, icon: TrendingUp, label: 'Budget Variance' },
          { to: `/clients/${clientId}/assets`, icon: Boxes, label: 'Fixed Assets' },
          { to: `/clients/${clientId}/tracking`, icon: Tag, label: 'Tracking' },
        ] : []),
        { to: `/clients/${clientId}/activity`, icon: Activity, label: 'Activity' },
      ],
    },
    {
      label: null,
      items: [
        { to: `/clients/${clientId}/settings`, icon: SlidersHorizontal, label: 'Client Settings' },
      ],
    },
  ] : []

  return (
    <div className="min-h-screen flex flex-col">
      {/* Xero-style Top Navigation Bar */}
      <header className="h-16 bg-[#0078C8] flex items-center justify-between px-4 sticky top-0 z-[1000] flex-shrink-0">
        {/* Left: Hamburger + Logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-[rgba(0,10,30,0.1)] transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-bold text-[15px] tracking-tight">LedgerPro</span>
          </Link>

          {/* Firm selector in top bar */}
          {currentFirm && firms.length > 1 && (
            <select
              className="ml-4 bg-white/10 text-white text-[13px] border border-white/20 rounded-[3px] px-3 py-1.5 focus:outline-none appearance-none cursor-pointer"
              value={currentFirm.id}
              onChange={(e) => {
                const firm = firms.find((f) => f.id === e.target.value)
                if (firm) setCurrentFirm(firm)
              }}
            >
              {firms.map((f) => (
                <option key={f.id} value={f.id} className="text-[#000A1E] bg-white">{f.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Right: Quick add + Search + Notifications + User */}
        <div className="flex items-center gap-1">
          {isClientContext && (
            <DropdownMenu>
              <DropdownMenuTrigger className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-[rgba(0,10,30,0.1)] transition-colors outline-none">
                <Plus className="w-5 h-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom" align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate(`/clients/${clientId}/contacts/new`)} className="gap-2 cursor-pointer">
                  <UserCircle className="w-4 h-4" /> New Contact
                </DropdownMenuItem>
                {isPremium && (
                  <>
                    <DropdownMenuItem onClick={() => navigate(`/clients/${clientId}/invoices/new`)} className="gap-2 cursor-pointer">
                      <FileText className="w-4 h-4" /> New Invoice
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/clients/${clientId}/bills/new`)} className="gap-2 cursor-pointer">
                      <Receipt className="w-4 h-4" /> New Bill
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/clients/${clientId}/quotes/new`)} className="gap-2 cursor-pointer">
                      <ScrollText className="w-4 h-4" /> New Quote
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <button className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-[rgba(0,10,30,0.1)] transition-colors">
            <Search className="w-5 h-5" />
          </button>
          <button className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-[rgba(0,10,30,0.1)] transition-colors">
            <Bell className="w-5 h-5" />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[rgba(0,10,30,0.1)] transition-colors outline-none ml-1">
              <Avatar size="sm">
                <AvatarFallback className="bg-white/20 text-white text-xs font-bold">
                  {(user?.full_name || user?.email || '?')[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-[13px] font-bold text-[#000A1E]">{user?.full_name || 'User'}</p>
                <p className="text-[12px] text-[rgba(0,10,30,0.75)]">{user?.email}</p>
                {currentFirm && <p className="text-[11px] text-[rgba(0,10,30,0.5)] mt-1">{currentFirm.name}</p>}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')} className="gap-2 cursor-pointer">
                <Settings className="w-4 h-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings/staff')} className="gap-2 cursor-pointer">
                <Users className="w-4 h-4" /> Staff
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings/billing')} className="gap-2 cursor-pointer">
                <CreditCard className="w-4 h-4" /> Billing
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={logout}
                className="gap-2 cursor-pointer"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Xero-style Light Sidebar */}
        {sidebarOpen && (
          <aside className="w-[300px] bg-[#FCFCFC] text-[#002A46] flex flex-col flex-shrink-0 border-r border-[#CCCED2]">
            {/* Client context header */}
            {isClientContext && (
              <div className="px-4 pt-4 pb-2">
                <button
                  onClick={() => navigate('/clients')}
                  className="flex items-center gap-1.5 text-[13px] text-[#002A46] hover:text-[#0078C8] transition-colors mb-3"
                >
                  <ArrowLeft className="w-4 h-4" /> All Clients
                </button>
                <div className="px-3 py-2 bg-white rounded-[3px] border border-[#CCCED2]">
                  <p className="text-[15px] text-[#000A1E] font-bold truncate">
                    {currentClient?.name || 'Client'}
                  </p>
                  <p className="text-[11px] text-[rgba(0,10,30,0.75)] capitalize">
                    {currentClient?.entity_type?.replace('_', ' ')} &middot; {currentClient?.currency}
                  </p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 px-4 py-4 overflow-y-auto">
              {!isClientContext ? (
                <ul className="space-y-0.5">
                  {firmNav.map((item) => (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className={`flex items-center gap-3 px-4 py-3 rounded-[3px] text-[15px] transition-colors ${
                          isActive(item.to)
                            ? 'bg-[#F0F5FF] text-[#0078C8] font-bold'
                            : 'text-[#002A46] hover:bg-[#F2F3F4]'
                        }`}
                      >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="space-y-5">
                  {clientNavGroups.map((group, gi) => (
                    <div key={gi}>
                      {group.label && (
                        <p className="px-4 mb-2 text-[11px] font-bold text-[#002A46] uppercase tracking-wider">
                          {group.label}
                        </p>
                      )}
                      <ul className="space-y-0.5">
                        {group.items.map((item) => (
                          <li key={item.to}>
                            <Link
                              to={item.to}
                              className={`flex items-center gap-3 px-4 py-2.5 rounded-[3px] text-[15px] transition-colors ${
                                isActive(item.to)
                                  ? 'bg-[#F0F5FF] text-[#0078C8] font-bold'
                                  : 'text-[#002A46] hover:bg-[#F2F3F4]'
                              }`}
                            >
                              <item.icon className="w-5 h-5 flex-shrink-0" />
                              <span className="flex-1">{item.label}</span>
                              {'badge' in item && typeof item.badge === 'number' && item.badge > 0 && (
                                <span className="bg-[#FFF3CD] text-[#856404] text-[11px] font-bold rounded-[3px] px-2 py-0.5 leading-none">
                                  {item.badge}
                                </span>
                              )}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </nav>

            {/* Bottom: Firm info */}
            {!isClientContext && currentFirm && (
              <div className="px-4 pb-4 border-t border-[#CCCED2] pt-3">
                <div className="flex items-center gap-3 px-4 py-2">
                  <div className="w-8 h-8 bg-[#0078C8] rounded-[8%] flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] text-[#000A1E] font-bold truncate">{currentFirm.name}</p>
                    <p className="text-[11px] text-[rgba(0,10,30,0.75)]">Accounting Firm</p>
                  </div>
                </div>
              </div>
            )}
          </aside>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 bg-[#F2F3F4]">
          {/* Page content */}
          <main className="flex-1 overflow-auto">
            <div className="p-6 max-w-[1200px] mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}
